import { Suspense, lazy, useEffect, useState } from 'react';
import { matchRoute, type Route } from './routes.js';
import { shareEnabled } from './share/enabled.js';

/**
 * The recipient page — only in builds that have sharing.
 *
 * Same reasoning as the share dialog: a dead branch over a compile-time
 * constant means the page, the share client and the whole crypto package are
 * absent from a build with sharing off, rather than present and unreachable.
 */
const ReceivePage = shareEnabled()
  ? lazy(() => import('./pages/ReceivePage.js').then((m) => ({ default: m.ReceivePage })))
  : null;
import { Home } from './pages/Home.js';
import { ToolPage } from './pages/ToolPage.js';
import { DocPage } from './pages/DocPage.js';
import type { PageDetail } from './content/details.js';
import { NotFound } from './pages/NotFound.js';

/**
 * The route switch — plan §12.2.
 *
 * The editor is behind `lazy()` so it never reaches a content page's bundle.
 * That is not a nicety: the workspace pulls in the PDF engine, and the tool
 * landing pages are the SEO and ad surface, budgeted at 30 KB of JavaScript.
 * `scripts/check-bundle-budget.mjs` fails the build if it leaks.
 */
const Workspace = lazy(() => import('./workspace/Workspace.js'));


declare const __AD_MODE__: string;

export interface AppProps {
  /** Supplied by the prerender step; the browser reads it from location. */
  readonly route?: Route;
  /**
   * The active tool page's prose.
   *
   * Passed in rather than imported so the whole-catalogue content module never
   * enters a client bundle: the prerender supplies it, and the browser reads
   * it back from a JSON block in the page's own HTML.
   */
  readonly detail?: PageDetail | null;
}

export function App({ route: initial, detail = null }: AppProps) {
  const [route, setRoute] = useState<Route>(
    () => initial ?? matchRoute(window.location.pathname, window.location.search),
  );

  useEffect(() => {
    const onPop = () => setRoute(matchRoute(window.location.pathname, window.location.search));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  switch (route.kind) {
    case 'home':
      return <Home />;

    case 'tool':
      return <ToolPage slug={route.slug} detail={detail} />;

    case 'doc':
      return <DocPage slug={route.slug} />;

    case 'editor':
      return (
        <Suspense
          fallback={
            // Full-height so the editor does not appear to grow into place, and
            // no spinner: the chunk is local and this is usually one frame.
            <div className="flex h-screen items-center justify-center bg-canvas">
              <p className="font-mono-ui text-xs tracking-wide text-faint">
                loading the editor…
              </p>
            </div>
          }
        >
          <Workspace adMode={__AD_MODE__} openTool={route.toolId} />
        </Suspense>
      );

    case 'receive':
      if (!ReceivePage) return <NotFound path={window.location.pathname} />;
      return (
        <Suspense fallback={null}>
          <ReceivePage pathname={window.location.pathname} hash={window.location.hash} />
        </Suspense>
      );

    case 'notFound':
      return <NotFound path={route.path} />;
  }
}
